import React, { createContext, type FC, type ReactNode, use, useCallback, useEffect, useMemo } from "react";
import { child, type DataSnapshot } from "firebase/database";
import { refBanks } from "@domain/api/firebase/firebaseRootRefs";

import { type TTransactionData } from "@banks/types";
import { CompanyContext } from "@domain/context/CompanyContext";
import { type GQL } from "@binale-tech/shared";
import { useRecordsBank } from "@domain/context/accountingData/RecordsCtx";
import { type GenericRecord } from "@domain/models";
import { useMutation } from "@apollo/client/react";
import {
    bankingBankDelete,
    bankingBankMarkFavorite,
    bankingBankSave,
    bankingTransactionsCreate,
    bankingTransactionsDelete,
    bankingTransactionsUpdate,
} from "@domain/context/mutations/bankMutations.graphql";
import { useCollectionListener } from "@domain/context/hooks/useCollectionListener";
import { useGroupedCollectionListener } from "@domain/context/hooks/useGroupedCollectionListener";
import { logDatasetSize } from "@domain/utils/datasetSizeLogger";

export interface IBanksController {
    saveBank: (input: Omit<GQL.IBankSaveInput, "companyId">) => Promise<string>;
    deleteBank: (bankId: string) => Promise<void>;
    markFavoriteBank: (input: GQL.IBank) => Promise<void>;
    transactionsCreate: (bankId: string, input: GQL.ITransactionCreateInput[]) => Promise<void>;
    transactionUpdate: (bankId: string, input: GQL.ITransactionUpdateInput[]) => Promise<void>;
    transactionDelete: (bankId: string, input: GQL.IIDsCompanyIdInput) => Promise<void>;
}

type TBanksValue = {
    banks: GQL.IBank[];
    banksById: Map<string, GQL.IBank>;
};

type TBankTransactionsValue = {
    transactionsByBank: Map<string, Map<string, GQL.IBankingTransaction>>;
    transactionsData: Record<string, TTransactionData>;
    transactionMap: Map<string, GQL.IBankingTransaction>;
    transactionRecordsMap: Map<string, GenericRecord[]>;
};

const EMPTY_INNER_MAP: ReadonlyMap<string, GQL.IBankingTransaction> = new Map();

export const BanksContext = createContext<TBanksValue>({
    banks: [],
    banksById: new Map(),
});
export const BankTransactionsContext = createContext<TBankTransactionsValue>({
    transactionsByBank: new Map(),
    transactionsData: {},
    transactionMap: new Map(),
    transactionRecordsMap: new Map(),
});
export const BanksControlContext = createContext<IBanksController>({} as IBanksController);

export const useBankTransactions = (bankId: string | undefined) => {
    const { transactionsByBank } = use(BankTransactionsContext);
    const innerMap = bankId ? transactionsByBank.get(bankId) : undefined;
    return useMemo(() => {
        const list: GQL.IBankingTransaction[] = [];
        const csvLineSet = new Set<string>();
        if (innerMap) {
            for (const t of innerMap.values()) {
                list.push(t);
                if (t.csvLine) {
                    csvLineSet.add(t.csvLine);
                }
            }
        }
        return {
            list,
            byId: innerMap ?? EMPTY_INNER_MAP,
            csvLineSet,
        };
    }, [innerMap]);
};

type TProps = {
    children: ReactNode;
};

export const BanksContextProvider: FC<TProps> = ({ children }) => {
    const recordsBank = useRecordsBank();

    const { companyGQL } = use(CompanyContext);
    const companyId = companyGQL?.id;

    const [saveBankMutation] = useMutation(bankingBankSave);
    const [deleteBankMutation] = useMutation(bankingBankDelete);

    const [markFavoriteMutation] = useMutation(bankingBankMarkFavorite);
    const [transactionsCreateMutation] = useMutation(bankingTransactionsCreate);
    const [transactionsUpdateMutation] = useMutation(bankingTransactionsUpdate);
    const [transactionsDeleteMutation] = useMutation(bankingTransactionsDelete);

    const banksRef = useMemo(() => child(refBanks, `${companyId}/banks`), [companyId]);
    const bankInitializer = useCallback((snap: DataSnapshot): GQL.IBank => snap.val(), []);
    const { list: banks } = useCollectionListener(banksRef, bankInitializer, !companyId);

    const transactionsBaseRef = useMemo(() => child(refBanks, `${companyId}/transactions`), [companyId]);
    const bankIds = useMemo(() => banks.map((b) => ({ groupId: b.id, subPath: b.id })), [banks]);
    const transactionInitializer = useCallback((snap: DataSnapshot): GQL.IBankingTransaction => snap.val(), []);
    const { state: transactionsByBank } = useGroupedCollectionListener(
        transactionsBaseRef,
        bankIds,
        transactionInitializer,
        !companyId,
    );

    const banksById = useMemo(() => {
        const map = new Map<string, GQL.IBank>();
        for (const b of banks) {
            map.set(b.id, b);
        }
        return map;
    }, [banks]);

    const transactionsData = useMemo<Record<string, TTransactionData>>(() => {
        const result: Record<string, TTransactionData> = {};
        transactionsByBank.forEach((group, bankId) => {
            result[bankId] = Object.fromEntries(group);
        });
        return result;
    }, [transactionsByBank]);

    const transactionMap = useMemo(() => {
        const map = new Map<string, GQL.IBankingTransaction>();
        for (const innerMap of transactionsByBank.values()) {
            for (const t of innerMap.values()) {
                map.set(t.id, t);
            }
        }
        return map;
    }, [transactionsByBank]);

    useEffect(() => {
        if (transactionMap.size > 0) {
            logDatasetSize("BankTransactionsContext.transactionMap", { type: "map", map: transactionMap });
        }
    }, [transactionMap]);

    const transactionRecordsMap = useMemo(() => {
        const map = new Map<string, GenericRecord[]>();
        recordsBank.combinedList.forEach((r) => {
            if (r.bankingTransactionId) {
                const existing = map.get(r.bankingTransactionId);
                if (existing) {
                    existing.push(r);
                } else {
                    map.set(r.bankingTransactionId, [r]);
                }
            }
        });
        return map;
    }, [recordsBank]);

    const banksValue: TBanksValue = useMemo(() => ({ banks, banksById }), [banks, banksById]);

    const transactionsValue: TBankTransactionsValue = useMemo(
        () => ({ transactionsByBank, transactionsData, transactionMap, transactionRecordsMap }),
        [transactionsByBank, transactionsData, transactionMap, transactionRecordsMap],
    );

    const actions: IBanksController = useMemo(
        () =>
            ({
                saveBank: async (input) => {
                    const res = await saveBankMutation({
                        variables: { input: { ...input, companyId } },
                    });
                    return res.data?.bankingBankSave?.id;
                },
                deleteBank: async (bankId) => {
                    await deleteBankMutation({
                        variables: { input: { id: bankId, companyId } },
                    });
                },
                markFavoriteBank: async (input) => {
                    await markFavoriteMutation({
                        variables: { input: { id: input.id, companyId } },
                    });
                },
                transactionsCreate: async (bankId, input) => {
                    await transactionsCreateMutation({
                        variables: { companyId, bankId, input },
                    });
                },
                transactionUpdate: async (bankId, input) => {
                    await transactionsUpdateMutation({
                        variables: { companyId, bankId, input },
                    });
                },
                transactionDelete: async (bankId, input) => {
                    await transactionsDeleteMutation({
                        variables: { ids: input, bankId },
                    });
                },
            }) satisfies IBanksController,
        [
            saveBankMutation,
            companyId,
            deleteBankMutation,
            markFavoriteMutation,
            transactionsCreateMutation,
            transactionsUpdateMutation,
            transactionsDeleteMutation,
        ],
    );

    return (
        <BanksContext value={banksValue}>
            <BankTransactionsContext value={transactionsValue}>
                <BanksControlContext value={actions}>{children}</BanksControlContext>
            </BankTransactionsContext>
        </BanksContext>
    );
};
